import type { Operation, OperationCounts, OperationCoverage, OperationItem, OperationState } from '@/lib/api/schemas/operations.schema'
import { unavailableReasonText } from '@/lib/warehouse/labels'

export const OPERATION_STATE_LABELS: Record<OperationState, string> = {
  queued: 'В очереди',
  running: 'Выполняется',
  completed: 'Завершена',
  completed_with_failures: 'Завершена с ошибками',
  failed: 'Сбой',
  cancelled: 'Отменена',
}

export const OPERATION_STATE_TONES: Record<OperationState, string> = {
  queued: 'bg-bone text-ink-2',
  running: 'bg-st-delivery/10 text-st-delivery',
  completed: 'bg-received-wash text-received',
  completed_with_failures: 'bg-st-unpaid/10 text-st-unpaid',
  failed: 'bg-st-unpaid/10 text-st-unpaid',
  cancelled: 'bg-bone text-ink-muted',
}

const KIND_LABELS: Record<string, string> = {
  wildberries_publish: 'Заливка на Wildberries',
  ymarket_publish: 'Заливка на Яндекс Маркет',
  ozon_publish: 'Заливка на Ozon',
  ymarket_price_collect: 'Сбор цен Яндекс Маркета',
  ymarket_price_sync: 'Цены на Яндекс Маркете',
  onec_piece_cost_sync: 'Себестоимость из 1С',
  ymarket_settlement_collect: 'Расчёты Маркета для юнит-экономики',
  warehouse_status_projection_backfill: 'Пересчёт статусов склада',
}

const COVERAGE_INPUT_LABELS: Record<string, string> = {
  match: 'найдено по УИН',
  cost: 'себестоимость',
  tariff: 'тарифы',
  bids: 'ставки',
  buyer_price: 'цена покупателя',
  package_ymarket_card: 'габариты с Маркета',
  package_wildberries_card: 'габариты с Wildberries',
  package_owner_default: 'габариты по умолчанию',
}

function coverageInputLabel(key: string): string {
  return COVERAGE_INPUT_LABELS[key] ?? key
}

/** One compact line: each input as a percentage with its covered/total counts. */
export function coverageLine(coverage: readonly OperationCoverage[]): string | null {
  if (coverage.length === 0) return null
  return coverage
    .map((entry) => `${coverageInputLabel(entry.key)} ${entry.percent === null ? '—' : `${entry.percent}%`} (${entry.covered}/${entry.total})`)
    .join(' · ')
}

/** Why the uncovered rest stayed empty, largest reason first, for the line's tooltip. */
export function coverageReasons(coverage: readonly OperationCoverage[]): string {
  return coverage
    .filter((entry) => Object.keys(entry.reasons).length > 0)
    .map((entry) => `${coverageInputLabel(entry.key)}: ${Object.entries(entry.reasons)
      .sort(([, left], [, right]) => right - left)
      .map(([code, count]) => `${unavailableReasonText(code)} — ${count}`)
      .join('; ')}`)
    .join(' | ')
}

export function operationKindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? 'Операция'
}

export function operationTitle(operation: Pick<Operation, 'kind' | 'title'>): string {
  const title = operation.title.trim()
  return title.length > 0 && /[А-Яа-яЁё]/u.test(title) ? title : operationKindLabel(operation.kind)
}

export function isActiveOperation(operation: Pick<Operation, 'state'>): boolean {
  return operation.state === 'queued' || operation.state === 'running'
}

export function processedCount(counts: OperationCounts): number {
  return Math.max(0, counts.total - counts.pending - counts.running)
}

export function progressPercent(counts: OperationCounts): number {
  if (counts.total <= 0) return 0
  return Math.min(100, Math.round((processedCount(counts) / counts.total) * 100))
}

export type ItemOutcome =
  | 'pending'
  | 'awaiting_confirmation'
  | 'awaiting_media'
  | 'running'
  | 'ok'
  | 'ok_with_warnings'
  | 'mismatch'
  | 'failed'
  | 'skipped'

/** A marketplace accepted the card but has not shown it yet; the queue checks again later. */
const AWAITING_CONFIRMATION_CODE = 'publication_unconfirmed'
/**
 * Media were uploaded once and the queue only re-reads the card, or the marketplace
 * refused them while the new card was not ready and the queue uploads them again.
 */
const AWAITING_MEDIA_CODES: ReadonlySet<string> = new Set(['media_listing_pending', 'media_card_not_ready'])

export function itemOutcome(item: Pick<OperationItem, 'state' | 'warnings' | 'waitingReasonCode' | 'reasonCode'>): ItemOutcome {
  if (item.state === 'ok') return item.warnings.length > 0 ? 'ok_with_warnings' : 'ok'
  if (item.state === 'failed' && item.reasonCode === 'readback_mismatch') return 'mismatch'
  if (item.state === 'pending' && item.waitingReasonCode === AWAITING_CONFIRMATION_CODE) return 'awaiting_confirmation'
  if (item.state === 'pending' && item.waitingReasonCode !== null && AWAITING_MEDIA_CODES.has(item.waitingReasonCode)) {
    return 'awaiting_media'
  }
  return item.state
}

export const ITEM_OUTCOME_LABELS: Record<ItemOutcome, string> = {
  pending: 'Ожидает',
  awaiting_confirmation: 'Ожидает подтверждения',
  awaiting_media: 'Ожидает подтверждения медиа',
  running: 'Выполняется',
  ok: 'Готово',
  ok_with_warnings: 'Готово с замечаниями',
  mismatch: 'Несовпадение',
  failed: 'Ошибка',
  skipped: 'Пропущено',
}

export const ITEM_OUTCOME_TONES: Record<ItemOutcome, string> = {
  pending: 'bg-bone text-ink-muted',
  awaiting_confirmation: 'bg-expected-wash text-gold-deep',
  awaiting_media: 'bg-expected-wash text-gold-deep',
  running: 'bg-st-delivery/10 text-st-delivery',
  ok: 'bg-received-wash text-received',
  ok_with_warnings: 'bg-expected-wash text-gold-deep',
  mismatch: 'bg-expected-wash text-gold-deep',
  failed: 'bg-st-unpaid/10 text-st-unpaid',
  skipped: 'bg-bone text-ink-2',
}

const REASON_LABELS: Record<string, string> = {
  image_unfixable: 'Фото невозможно исправить автоматически',
  video_broken: 'Видео повреждено',
  video_incompatible: 'Видео не подходит маркетплейсу',
  characteristic_missing: 'Не заполнена обязательная характеристика',
  provider_validation: 'Маркетплейс отклонил карточку',
  rate_limited: 'Маркетплейс ограничил частоту запросов, повторы исчерпаны',
  media_url_not_public: 'Ссылка на медиа недоступна маркетплейсу',
  readiness_blocked: 'Изделие не готово к публикации',
  readback_mismatch: 'Медиа в карточке не совпали с отправленными',
  media_card_not_ready: 'Маркетплейс не принял медиа: карточка не стала готова к их приёму',
  publication_withdrawn: 'Карточка удалена с маркетплейса',
  cancelled: 'Отменено оператором',
  weight_not_measured: 'Нет измеренного веса изделия',
  price_quarantine_risk: 'Изменение цены может попасть в карантин Маркета',
  price_quarantined: 'Маркет поместил цену в карантин; нужно подтверждение',
  promo_update_rejected: 'Маркет не принял изменение участия в акции',
  wb_vendor_code_in_trash: 'Карточка WB в корзине и не может быть восстановлена',
  wb_vendor_code_recovered: 'Карточка WB восстановлена из корзины',
  promo_price_below_minimum: 'Цена в акции ниже минимума для акций',
  provider_mutations_disabled: 'Изменение данных на площадках выключено',
  price_switch_off: 'Изменение цен Маркета выключено владельцем',
  preview_stale: 'Предпросмотр устарел',
}

/** Every failed or skipped item names a cause, even when the server sent only a code. */
export function itemReasonText(item: Pick<OperationItem, 'reason' | 'reasonCode'>): string | null {
  const reason = item.reason?.trim()
  if (reason) return reason
  if (item.reasonCode === null) return null
  return REASON_LABELS[item.reasonCode] ?? 'Причина не расшифрована'
}

const MARKETPLACE_ID_LABELS: Record<string, string> = {
  nmId: 'Артикул WB',
  vendorCode: 'Артикул продавца',
  offerId: 'Идентификатор Маркета',
  sku: 'SKU',
}

export function marketplaceIdLabel(key: string): string {
  return MARKETPLACE_ID_LABELS[key] ?? key
}

export function formatOperationTime(value: string | null): string | null {
  if (value === null) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
